  <template>
  <div class="ielts-page">

    <!-- HEADER -->
    <div class="header d-flex justify-content-between align-items-center px-4">
      <div class="d-flex align-items-center gap-1">
        <div class="logo"><img src="../assets/Ieltslogo.png" alt="IELTS Logo"></div>
        <div>
          <div class="fw-bold" style="font-weight: 700;">Test taker ID</div>
          <div class="audio">
            <i class="fa-solid fa-volume-high fa-2xs"></i>
              <span class="audio-text">Audio is Playing</span>
              </div>
        </div>
      </div>

     
        <div class="icons">
          <i class="fa-solid fa-signal no-hover"></i>
          <i class="fa-regular fa-bell"></i>
          <i class="fa-solid fa-bars"></i>
        </div>
     
    </div>

    <router-view />

      <!-- FLOATING NAV BUTTONS -->
<div class="floating-nav">
  <button class="nav-btn" @click="prevQuestion">
    <svg viewBox="0 0 24 24" class="arrow-icon">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  </button>

  <button class="nav-btn dark" @click="nextQuestion">
    <svg viewBox="0 0 24 24" class="arrow-icon">
      <path d="M9 6l6 6-6 6" />
    </svg>
  </button>
</div>


      <!-- FOOTER -->
<div class="footer">

  <div class="parts-table">

    <div
      v-for="part in 4"
      :key="part"
      class="part-box-footer"
      :class="{ active: activePart === part }"
     @click="goToPart(part)"
    >
     <div class="part-title">
  Part {{ part }}
</div>

<div class="question-list">
  <template v-if="activePart === part">
    <span
      v-for="n in getPartQuestions(part)"
      :key="n"
      class="q-cell"
      :class="{ active: activeQuestion === n }"
      @click.stop="goToQuestion(n)"
    >
      {{ n }}
    </span>
  </template>

  <template v-else>
    <span class="part-progress">
      {{ getAnsweredCount(part) }} of 10
    </span>
  </template>
</div>
      </div>
    </div>

  
  <div class="footer-check">
    <div class="check-box">
      <i class="fa-solid fa-check"></i>
    </div>
  </div>
 

</div>
<!-- INTRO OVERLAY -->
<div v-if="showIntroOverlay" class="intro-overlay">
  <div class="intro-box">
    <div class="headphone-icon">
      <i class="fa-solid fa-headphones"></i>
    </div>

    <p class="intro-text">
      You will be listening to an audio clip during this test. You will not be permitted to pause or rewind the audio while answering the questions.
    </p>

    <p class="intro-subtext">
      To continue, click Play.
    </p>

    <button class="play-btn" @click="startTest">
      <i class="fa-solid fa-circle-play"></i>
      Play
    </button>
  </div>
</div>


    </div>
    
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { computed } from 'vue'
import { reactive, provide } from 'vue'
import { useExamSession } from '@/composables/useExamSession'
import { getListeningExam } from '@/api/listening'

const TOTAL_QUESTIONS = 40
const QUESTIONS_PER_PART = 10
const router = useRouter()
const route = useRoute()
const answers = reactive({})
provide('answers', answers)
const activeQuestion = ref(1)
provide('activeQuestion', activeQuestion)

// ---- Backend session integration (autosave / restore / submit / timer) ----
// Degrades to local-only behavior when the user is not authenticated.
const buildAutosave = () =>
  Object.entries(answers)
    .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    .map(([k, v]) => ({ question_number: Number(k), value: v }))

const examSession = useExamSession({
  skill: 'listening',
  level: route.params.level,
  number: route.params.testId,
  fetchExam: getListeningExam,
  buildAutosave,
  buildSubmit: () => ({ answers: buildAutosave(), writing: [] }),
  hydrate: (detail) => {
    (detail.answers || []).forEach((a) => {
      answers[a.question_number] = a.value
    })
  },
})

provide('examSession', examSession)
provide('examTimeLeft', examSession.formattedTimeLeft)

onMounted(examSession.init)
watch(answers, () => examSession.scheduleAutosave(), { deep: true })

const goToQuestion = (n) => {
  activeQuestion.value = n

  const targetPart = getPartByQuestion(n)

  if (targetPart !== activePart.value) {
    goToPart(targetPart)
  }
}
const getPartByQuestion = (q) => {
  return Math.ceil(q / QUESTIONS_PER_PART)
}

const getPartQuestions = (part) => {
  const start = (part - 1) * QUESTIONS_PER_PART + 1
  const end = part * QUESTIONS_PER_PART
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}
const activePart = computed(() => {
  if (route.path.includes('part-2')) return 2
  if (route.path.includes('part-3')) return 3
  if (route.path.includes('part-4')) return 4
  return 1
})

const goToPart = (part) => {
  const base = `/listening-exam/${route.params.level}/${route.params.testId}`

        
  const startQuestion = (part - 1) * QUESTIONS_PER_PART + 1
  activeQuestion.value = startQuestion

  if (part === 1) {
    router.push(base)
  } else {
    router.push(`${base}/part-${part}`)
  }
}




const showIntroOverlay = ref(true)

const startTest = () => {
  showIntroOverlay.value = false
}




//  navigation
const nextQuestion = () => {
  if (activeQuestion.value < TOTAL_QUESTIONS) {
    goToQuestion(activeQuestion.value + 1)
  }
}

const prevQuestion = () => {
  if (activeQuestion.value > 1) {
    goToQuestion(activeQuestion.value - 1)
  }
}
const getAnsweredCount = (part) => {
  const start = (part - 1) * 10 + 1
  const end = part * 10

  let count = 0

  for (let i = start; i <= end; i++) {
    if (answers[i] && answers[i].toString().trim() !== '') {
      count++
    }
  }

  return count
}
import { watchEffect } from 'vue'

watchEffect(() => {
  const part = activePart.value
  const start = (part - 1) * QUESTIONS_PER_PART + 1
  const end = part * QUESTIONS_PER_PART

  if (
    activeQuestion.value < start ||
    activeQuestion.value > end
  ) {
    activeQuestion.value = start
  }
})


</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
}
.ielts-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.header {
  height: 60px;
  background: white;
  border-bottom: 1px solid #ddd;
}
.logo img {
  height: 100px;
  width: auto;
  display: block;
  margin-left: -35px;
}
.icons {
  display: flex;
  gap: 10px;
  align-items: center;
}
.icons i {
  font-size: 25px;
  color: #333;
  cursor: pointer;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.icons i:hover {
  background: #F2F2F2;

}
.icons i.no-hover:hover {
  background: transparent !important;
}
.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 35px 20px;
  background: #fff;
}

.footer {
  display: flex;
  padding: 0;
  background: #fff;
  height: 48px;
  align-items: flex-end;
  gap: 2px;
  overflow: hidden;
}
.audio {
  color: #000;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 6px;
}
.audio-text {
  font-size: smaller;
}

.parts-table {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  height: auto;   
  gap: 0;
}
.parts-table > div {
  min-width: 0;
}
.footer-check {
  flex-shrink: 0;
}
.part-box-footer {
  display: flex;
  align-items: center;
  justify-content: center;   
  gap: 8px;
  width: 100%;
  height: 48px;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;      
      
}
.part-box-footer.active {
   justify-content: flex-start;
   padding-left: 5px;
}
.part-box-footer:not(.active):hover {
  background: #EFEFEF;
}
.part-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}
.part-title,
.part-progress {
  white-space: nowrap;
}
.question-list {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
}
.part-progress {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}
.footer {
  overflow: visible;
}

.floating-nav {
  position: fixed;
  right: 20px;
  bottom: 160px; 
  display: flex;
  gap: 10px;
  z-index: 1000;
}
.arrow-icon {
  width: 24px;
  height: 24px;
  stroke: white;
  stroke-width: 2.5;
  fill: none;
}
.nav-btn {
  width: 50px;
  height: 50px;
  background: #5a5a5a;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.nav-btn.dark {
  background: black;
}
.nav-btn:hover {
  transform: translateY(-2px);
}
.nav-btn:not(.dark):hover {
  background: #4a4a4a;
}
.nav-btn.dark:hover {
  background: #222;
}
.check-box {
  width: 50px;
  height: 48px;
  background: #EFEFEF;
  border: 2px solid #0000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.check-box i {
  color: #535353;
  font-size: 18px;
  
}
.check-box:hover {
  background: #000;
}
.check-box:hover i {
  color: #fff;
}


.intro-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.intro-box {
  text-align: center;
  max-width: 700px;
  color: white;
  padding: 20px;
}

.headphone-icon {
  font-size: 90px;
  margin-bottom: 30px;
}

.intro-text {
  font-size: 17px;
  line-height: 1.7;
  margin-bottom: 15px;
  white-space: nowrap;
  transform: translateX(-185px);
  
}

.intro-subtext {
  font-size: 17px;
  margin-bottom: 20px;
}

.play-btn {
  background: black;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 3px;
  font-size: 17px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
 
}

.play-btn:hover {
  background: #404040;
}
.q-cell {
  font-size: 18px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.q-cell:hover {
   border: 2px solid #418FC6;
   background: #FFFFFF;
   color:#000;
   font-weight: 600;
}
.q-cell.active {
  border: 2px solid #418FC6;
  color: #000;
  font-weight: 600;
  background: #FFFFFF;
}
@media (max-width: 1024px) {
  .header, .footer {
    padding: 0 10px;
  }
}
@media (max-width: 768px) {
  .header {
    height: 50px;
    padding: 5px 10px;
  }

  .logo img {
    height: 40px;
    margin-left: 0;
  }

  .header .fw-bold {
    font-size: 12px;
    margin-top: 0;
  }

  .icons i {
    width: 35px;
    height: 35px;
    font-size: 16px;
  }

  .icons {
    gap: 5px;
  }
}
@media (max-width: 768px) {
  .footer {
    height: auto;
    flex-direction: column;
    align-items: stretch;
  }

  .parts-table {
    grid-template-columns: 1fr;
  }

  .part-box-footer {
    justify-content: space-between;
    padding: 8px 10px;
    height: auto;
  }

  .question-list {
    flex-wrap: wrap;
  }

  .q-cell {
    font-size: 14px;
    padding: 3px 5px;
  }

  .part-title {
    font-size: 14px;
  }

  .part-progress {
    font-size: 12px;
  }

  .footer-check {
    display: none; /* مهم: روی موبایل حذفش کن */
  }
}
@media (max-width: 768px) {
  .floating-nav {
    bottom: 90px;
    right: 10px;
    gap: 6px;
  }

  .nav-btn {
    width: 42px;
    height: 42px;
  }

  .arrow-icon {
    width: 20px;
    height: 20px;
  }
}
@media (max-width: 768px) {
  .parts-table {
    display: flex;
    overflow-x: auto;
    gap: 10px;
  }

  .part-box-footer {
    min-width: 220px;
    flex-shrink: 0;
    border: 1px solid #eee;
    border-radius: 6px;
  }
}

</style>
